import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faListUl, faPen } from '@fortawesome/pro-regular-svg-icons'
import { makeStyles } from 'tss-react/mui'
import { SectionWrapper } from '../../../../common/components/SectionWrapper/SectionWrapper'
import {
  DetailRow,
  DetailInfo,
} from '../../../../common/components/DetailRow/DetailRow'
import { UpliftPercentageModal } from '../../../../common/components/UpliftPercentageModal/UpliftPercentageModal'
import {
  formatPriceUpliftYesNo,
  formatRenewalDeadlineDate,
  formatRenewalOptOut,
  formatRenewalsAllowed,
  formatUpliftPercent,
  getContractEndIso,
  resolveRenewalPricingTerms,
  shouldShowRenewalDetailsFromTerms,
  type ContractEndLike,
} from '../../../../common/utils/renewalDetailsFormat'
import { SubscriptionDetailObject } from '../../../../modules/subscription/reducer'
import { getSubscriptionRenewalDetailsItem } from '../subscriptionDetailHelpers'

const useStyles = makeStyles()(theme => ({
  finalUpliftValue: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  finalUpliftCell: {
    overflow: 'visible',
    whiteSpace: 'normal',
  },
  editButton: {
    padding: 0,
    margin: 0,
    border: 'none',
    background: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 0,
  },
  editIcon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
  },
}))

interface RenewalDetailsSectionProps {
  subscriptionDetail: SubscriptionDetailObject
  classes: { sectionContent?: string }
  timeZone?: string
  /** ponytail: wire to API when backend supports entered uplift % */
  onSubmitFinalUplift?: (percent: number) => void
}

export const RenewalDetailsSection: React.FC<RenewalDetailsSectionProps> = ({
  subscriptionDetail,
  classes,
  timeZone = 'UTC',
  onSubmitFinalUplift,
}) => {
  const { classes: localClasses } = useStyles()
  const subscription = getSubscriptionRenewalDetailsItem(subscriptionDetail)
  const [modalOpen, setModalOpen] = useState(false)
  const [localUplift, setLocalUplift] = useState<number | null>(null)

  if (!shouldShowRenewalDetailsFromTerms(subscription)) return null

  const isRange =
    resolveRenewalPricingTerms(subscription) === 'PRICE_UPLIFT' &&
    (subscription.priceUpliftType ?? 'FIXED') === 'RANGE'
  const endIso = getContractEndIso(subscription as ContractEndLike)
  const displayedUplift =
    localUplift ?? subscription.enteredUpliftPercent ?? null

  return (
    <SectionWrapper heading="Renewal details" icon={faListUl}>
      <div
        className={classes.sectionContent}
        data-testid="subscription-renewal-details"
      >
        <DetailRow borderBottom props={{ spacing: 1 }}>
          <>
            <DetailInfo
              title="Number of renewals allowed"
              keyName="renewalsAllowed"
              value={formatRenewalsAllowed(subscription)}
            />
            <DetailInfo
              title="Opt out deadline date"
              keyName="optOutDeadlineDate"
              value={formatRenewalDeadlineDate(
                subscription.optOutDeadlineDays,
                endIso,
                timeZone
              )}
            />
          </>
        </DetailRow>
        <DetailRow borderBottom props={{ spacing: 1 }}>
          <>
            <DetailInfo
              title="Price uplift"
              keyName="priceUplift"
              value={formatPriceUpliftYesNo(subscription)}
            />
            <DetailInfo
              title="Price uplift %"
              keyName="priceUpliftPercent"
              value={formatUpliftPercent(subscription)}
            />
          </>
        </DetailRow>
        {isRange && (
          <>
            <DetailRow borderBottom props={{ spacing: 1 }}>
              <>
                <DetailInfo
                  title="Max adjustment date"
                  keyName="adjustmentDeadline"
                  value={formatRenewalDeadlineDate(
                    subscription.adjustmentDeadlineDays,
                    endIso,
                    timeZone
                  )}
                />
                <DetailInfo
                  title="Default uplift percentage"
                  keyName="defaultUpliftPercent"
                  value={
                    subscription.defaultUpliftPercent != null
                      ? `${subscription.defaultUpliftPercent}%`
                      : '-'
                  }
                />
              </>
            </DetailRow>
            <DetailRow borderBottom props={{ spacing: 1 }}>
              <>
                <DetailInfo
                  title="Final Uplift Percentage"
                  keyName="enteredUpliftPercent"
                  valueClassName={localClasses.finalUpliftCell}
                  value={
                    <span className={localClasses.finalUpliftValue}>
                      {displayedUplift != null ? `${displayedUplift}%` : '-'}
                      <button
                        type="button"
                        className={localClasses.editButton}
                        data-testid="final-uplift-edit"
                        aria-label="Edit final uplift percentage"
                        onClick={() => setModalOpen(true)}
                      >
                        <FontAwesomeIcon
                          icon={faPen}
                          className={localClasses.editIcon}
                        />
                      </button>
                    </span>
                  }
                />
                <DetailInfo
                  title="Seller Opt-out"
                  keyName="sellerOptOut"
                  value={formatRenewalOptOut(subscription.sellerOptOut)}
                />
              </>
            </DetailRow>
            <DetailRow borderBottom={false} props={{ spacing: 1 }}>
              <DetailInfo
                title="Buyer Opt-out"
                keyName="buyerOptOut"
                value={formatRenewalOptOut(subscription.buyerOptOut)}
              />
            </DetailRow>
            <UpliftPercentageModal
              open={modalOpen}
              minPercent={subscription.priceUpliftMinPercent}
              maxPercent={subscription.priceUpliftMaxPercent}
              initialPercent={displayedUplift}
              onClose={() => setModalOpen(false)}
              onSubmit={percent => {
                setLocalUplift(percent)
                onSubmitFinalUplift?.(percent)
              }}
            />
          </>
        )}
        {!isRange && (
          <DetailRow borderBottom={false} props={{ spacing: 1 }}>
            <>
              <DetailInfo
                title="Seller Opt-out"
                keyName="sellerOptOut"
                value={formatRenewalOptOut(subscription.sellerOptOut)}
              />
              <DetailInfo
                title="Buyer Opt-out"
                keyName="buyerOptOut"
                value={formatRenewalOptOut(subscription.buyerOptOut)}
              />
            </>
          </DetailRow>
        )}
      </div>
    </SectionWrapper>
  )
}
